import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Briefcase, ClipboardCheck, Eye, Hourglass, Mail, Search, Trash2, UserRound, Users } from 'lucide-react'
import Modal from '@components/shared/Modal'
import OverflowAwareContainer from '@components/shared/OverflowAwareContainer'
import ApiService from '../../services/api'
import { useNotification } from '../../context/NotificationContext'
import { SECTIONS } from './runtime/formularioSections'
import { calculateAverageScore } from './runtime/formularioRuntimeUtils'
import { DISCURSIVE_GUIDE, LEGACY_DISCURSIVE_QUESTIONS } from './runtime/formularioRhGuia'
import ProvasTecnicasFicha, { ProvaAutomaticaToggle, SELECAO_LABELS, SelecaoTecnicaChip } from './ProvasTecnicasFicha'
import { nivelExperienciaLabel } from './runtime/nivelExperiencia'
import './formulario-publico.css'

// Perguntas discursivas na ordem do questionário, para mostrar o título no lugar do código (DQ1, DQ2...).
const DISCURSIVE_QUESTIONS = SECTIONS.filter((s) => s.type === 'discursive').flatMap((s) => s.questions)
const LEGACY_IDS = new Set(LEGACY_DISCURSIVE_QUESTIONS.map((q) => q.id))

// Fichas que só respondem DQ1 a DQ3 são do questionário anterior, cujas perguntas tinham outro conteúdo.
function discursiveEntries(answers) {
  const keys = Object.keys(answers)
  const isLegacy = keys.length > 0 && keys.every((k) => LEGACY_IDS.has(k))
  const questions = isLegacy ? LEGACY_DISCURSIVE_QUESTIONS : DISCURSIVE_QUESTIONS
  const known = questions
    .filter((q) => q.id in answers)
    .map((q) => ({ key: q.id, title: q.title, code: q.id, text: q.text, observe: isLegacy ? null : DISCURSIVE_GUIDE[q.id], value: answers[q.id] }))
  // Respostas de perguntas que não existem mais no questionário, ou gravadas sem código ("undefined").
  const unknown = keys
    .filter((k) => !questions.some((q) => q.id === k))
    .map((k) => ({
      key: k,
      title: k === 'undefined' ? 'Resposta discursiva (ficha antiga, sem identificação da pergunta)' : 'Pergunta removida do questionário',
      code: k === 'undefined' ? '' : k,
      value: answers[k],
    }))
  return known.concat(unknown)
}

const DEFAULT_FILTERS = {
  nome: '',
  email: '',
  area: 'ALL',
  selecao: 'ALL',
  score: '',
  scoreMin: '',
  scoreMax: '',
}

// Converte nota digitada ou gravada em número (aceita vírgula). Vazio ou "-" vira NaN.
function toNumber(value) {
  if (value === null || value === undefined || value === '' || value === '-') return NaN
  return Number(String(value).replace(',', '.'))
}

function normalize(value) {
  return String(value || '').trim().toLocaleLowerCase('pt-BR')
}

function formatDateTime(value) {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('pt-BR')
}

function getInitials(name) {
  return String(name || 'Candidato')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'FC'
}

const SCORE_INFO = {
  H: ['Honestidade e humildade', 'Tendência declarada à sinceridade, modéstia e menor interesse em vantagens indevidas.'],
  E: ['Emotividade', 'Sensibilidade emocional e necessidade de apoio percebidas nas respostas; maior não significa melhor.'],
  X: ['Extroversão', 'Sociabilidade e disposição para interação; diferentes funções exigem estilos diferentes.'],
  A: ['Amabilidade', 'Disposição para conciliação, paciência e tolerância em desacordos.'],
  C: ['Organização e disciplina', 'Autopercepção de planejamento, cuidado e cumprimento de tarefas.'],
  O: ['Abertura a experiências', 'Curiosidade, flexibilidade intelectual e interesse por novas ideias.'],
  promotion: ['Foco em oportunidades', 'Maior orientação a conquistas, avanços e possibilidades.'],
  prevention: ['Foco em prevenção', 'Maior orientação a cuidados, regras e prevenção de falhas.'],
  autonomy: ['Autonomia percebida', 'Sensação de liberdade e participação nas próprias decisões de trabalho.'],
  competence: ['Competência percebida', 'Quanto a pessoa relata sentir-se capaz de enfrentar desafios.'],
  relatedness: ['Conexão com pessoas', 'Percepção de vínculo e pertencimento em contextos de trabalho.'],
  CI: ['Constância de interesses', 'Tendência declarada a manter interesses e objetivos ao longo do tempo.'],
  PE: ['Persistência no esforço', 'Tendência declarada a seguir trabalhando apesar de dificuldades.'],
  grit: ['Perseverança geral', 'Resumo das dimensões de constância e persistência.'],
  proactivity: ['Iniciativa em situações', 'Pontuação derivada das escolhas em cenários simulados.'],
  integrity: ['Critério ético em situações', 'Pontuação derivada das alternativas escolhidas em dilemas simulados; não comprova integridade pessoal.'],
  teamwork: ['Colaboração em situações', 'Pontuação das escolhas envolvendo cooperação e trabalho em equipe.'],
}

// Nota média das questões de múltipla escolha (formularioRuntimeUtils.calculateAverageScore), calculada uma vez por ficha.
function withNotaMedia(ficha) {
  return { ...ficha, notaMedia: calculateAverageScore(SECTIONS, ficha.answers, ficha.sjtAnswers) }
}

const NOTA_MEDIA_INFO = 'Média dos pontos das questões de múltipla escolha do questionário (escalas e cenários), de 1 a 5. Itens invertidos são pontuados ao contrário.'

// Pontuações do filtro: nota média e notas comportamentais (0 a 5) e índices experimentais (0 a 100).
const AVERAGE_FILTER = { key: 'notaMedia', label: 'Nota média', max: 5, get: (item) => item.notaMedia }
const INDEX_FILTERS = [
  { key: 'aderencia', label: 'Aderência estimada', max: 100, get: (item) => item.aderencia },
  { key: 'permanencia', label: 'Permanência estimada', max: 100, get: (item) => item.permanencia },
]
const BEHAVIOR_FILTERS = Object.entries(SCORE_INFO).map(([key, info]) => ({ key: `nota:${key}`, label: info[0], max: 5, get: (item) => item.scores?.[key] }))
const SCORE_FILTERS = [AVERAGE_FILTER, ...INDEX_FILTERS, ...BEHAVIOR_FILTERS]

function formatScore(value, max) {
  if (!Number.isFinite(value)) return '-'
  return max === 100 ? `${Math.round(value)} / 100` : `${value.toFixed(1).replace('.', ',')} / 5`
}

function ScoreBar({ label, value }) {
  const numeric = Number(value)
  const valid = value !== null && value !== undefined && value !== '' && Number.isFinite(numeric)
  const safe = valid ? Math.max(0, Math.min(5, numeric)) : 0
  const info = SCORE_INFO[label] || [label, 'Indicador exploratório do questionário.']
  const reading = !valid ? 'Sem dados' : safe < 2.5 ? 'Faixa inferior da escala' : safe < 3.5 ? 'Faixa intermediária' : 'Faixa superior da escala'
  return (
    <div className="submission-score-item score-explained">
      <div className="submission-score-header">
        <strong>{info[0]}</strong>
        <span className="score-value">{valid ? `${safe.toFixed(1).replace('.', ',')} / 5` : 'Sem dados'}</span>
      </div>
      <p className="score-description">{info[1]}</p>
      <div className="submission-progress-track" role="meter" aria-label={info[0]} aria-valuemin={0} aria-valuemax={5} aria-valuenow={valid ? safe : 0}>
        <span style={{ width: `${(safe / 5) * 100}%` }} />
      </div>
      <small className="score-reading">{reading}</small>
    </div>
  )
}

function ExperimentalMetric({ title, value, meaning, max = 100 }) {
  const numeric = Number(value)
  const valid = value !== null && value !== undefined && value !== '' && Number.isFinite(numeric)
  return (
    <div className="metric-explained">
      <span>{title}</span>
      <strong>{valid ? formatScore(numeric, max) : 'Sem dados'}</strong>
      <small>{meaning}</small>
    </div>
  )
}

function profileValue(profile, camelKey, pascalKey) {
  return profile?.[camelKey] ?? profile?.[pascalKey]
}

function ProfileList({ title, items }) {
  if (!Array.isArray(items) || items.length === 0) return null
  return (
    <article className="submission-profile-list">
      <strong>{title}</strong>
      <ul>
        {items.map((item) => <li key={item}>{item}</li>)}
      </ul>
    </article>
  )
}

export default function FormularioSubmissoesPage() {
  const { showError, showSuccess } = useNotification()
  const [loading, setLoading] = useState(true)
  const [items, setItems] = useState([])
  const [selected, setSelected] = useState(null)
  const [payload, setPayload] = useState(null)
  const [filters, setFilters] = useState(DEFAULT_FILTERS)

  const load = useCallback(async () => {
    setLoading(true)
    const result = await ApiService.getFichasFormularioPublico()
    setLoading(false)
    if (!result.success) return showError(result.error || 'Erro ao carregar fichas')
    setItems(Array.isArray(result.data) ? result.data.map(withNotaMedia) : [])
  }, [showError])

  useEffect(() => { load() }, [load])

  const openDetails = async (id) => {
    const result = await ApiService.getFichaFormularioPublico(id)
    if (!result.success) return showError(result.error || 'Erro ao carregar ficha')
    const ficha = withNotaMedia(result.data)
    setSelected(ficha)
    setPayload(ficha)
  }

  const remove = async (id) => {
    if (!window.confirm('Excluir esta ficha?')) return
    const result = await ApiService.deleteFichaFormularioPublico(id)
    if (!result.success) return showError(result.error || 'Erro ao excluir ficha')
    showSuccess('Ficha excluída com sucesso')
    if (selected?.id === id) {
      setSelected(null)
      setPayload(null)
    }
    load()
  }

  const areaOptions = useMemo(() => {
    const uniqueAreas = [...new Set(items.map((item) => item.areaPretendida).filter(Boolean))]
    return uniqueAreas.sort((a, b) => a.localeCompare(b, 'pt-BR'))
  }, [items])

  const scoreFilter = SCORE_FILTERS.find((s) => s.key === filters.score) || null

  const filteredItems = useMemo(() => {
    const nome = normalize(filters.nome)
    const email = normalize(filters.email)
    const area = filters.area
    const min = toNumber(filters.scoreMin)
    const max = toNumber(filters.scoreMax)

    const result = items.filter((item) => {
      const matchesName = !nome || normalize(item.nome).includes(nome)
      const matchesEmail = !email || normalize(item.email).includes(email)
      const matchesArea = area === 'ALL' || item.areaPretendida === area
      const matchesSelecao = filters.selecao === 'ALL' || (item.selecaoTecnica || 'pendente') === filters.selecao
      let matchesScore = true
      if (scoreFilter) {
        // Com uma pontuação escolhida, fichas sem essa nota ficam de fora.
        const value = toNumber(scoreFilter.get(item))
        matchesScore = Number.isFinite(value) && !(Number.isFinite(min) && value < min) && !(Number.isFinite(max) && value > max)
      }
      return matchesName && matchesEmail && matchesArea && matchesSelecao && matchesScore
    })
    // Com uma pontuação escolhida, a lista fica da maior para a menor nota.
    return scoreFilter ? result.sort((a, b) => toNumber(scoreFilter.get(b)) - toNumber(scoreFilter.get(a))) : result
  }, [filters, items, scoreFilter])

  // Triagem: "pendente" ainda aguarda a decisão do RH; aprovado ou não selecionado já foi triado.
  const aguardandoTriagem = useMemo(() => items.filter((item) => (item.selecaoTecnica || 'pendente') === 'pendente').length, [items])

  const stats = useMemo(() => ([
    { label: 'Fichas recebidas', value: items.length, icon: Users },
    { label: 'Aguardando triagem', value: aguardandoTriagem, icon: Hourglass },
    { label: 'Triagem concluída', value: items.length - aguardandoTriagem, icon: ClipboardCheck },
    { label: 'Nos filtros', value: filteredItems.length, icon: Search },
    { label: 'Áreas mapeadas', value: areaOptions.length, icon: Briefcase },
  ]), [aguardandoTriagem, areaOptions.length, filteredItems.length, items.length])

  const scoreEntries = useMemo(() => Object.entries(payload?.scores || {}), [payload])
  const profile = payload?.profile || payload?.Profile || {}

  const updateFilter = (field, value) => setFilters((prev) => ({ ...prev, [field]: value }))
  const clearFilters = () => setFilters(DEFAULT_FILTERS)
  const closeDetails = () => {
    setSelected(null)
    setPayload(null)
  }
  // A decisão do RH devolve a ficha atualizada: atualiza o detalhe aberto e a linha da tabela.
  const updateFicha = (ficha) => {
    setSelected(ficha)
    setPayload(ficha)
    setItems((prev) => prev.map((item) => (item.id === ficha.id ? { ...item, selecaoTecnica: ficha.selecaoTecnica, decisaoTecnicaEm: ficha.decisaoTecnicaEm } : item)))
  }
  const hasActiveFilters = filters.nome || filters.email || filters.area !== 'ALL' || filters.selecao !== 'ALL' || filters.score

  return (
    <div className="submissions-page">
      <div className="users-page-top">
        <section className="users-stats-grid submissions-stats-grid" aria-label="Resumo das fichas">
          {stats.map(({ label, value, icon: Icon }) => (
            <article className="users-stat-card" key={label}>
              <div>
                <span>{label}</span>
                <strong>{value}</strong>
              </div>
              <Icon size={20} aria-hidden="true" />
            </article>
          ))}
        </section>

        <section className="users-filter-card submissions-filter-card" aria-label="Filtros de fichas de candidatos">
          <div className="users-filter-grid submissions-filter-grid">
            <div className="users-select-group submissions-filter-field submissions-filter-field-wide">
              <label className="users-field-label" htmlFor="submission-name-filter">Nome do candidato</label>
              <div className="users-input-with-icon">
                <UserRound size={18} />
                <input
                  id="submission-name-filter"
                  className="users-text-input"
                  type="search"
                  placeholder="Ex.: Ana Silva"
                  value={filters.nome}
                  onChange={(event) => updateFilter('nome', event.target.value)}
                />
              </div>
            </div>

            <div className="users-select-group submissions-filter-field submissions-filter-field-wide">
              <label className="users-field-label" htmlFor="submission-email-filter">Email</label>
              <div className="users-input-with-icon">
                <Mail size={18} />
                <input
                  id="submission-email-filter"
                  className="users-text-input"
                  type="search"
                  placeholder="candidato@email.com"
                  value={filters.email}
                  onChange={(event) => updateFilter('email', event.target.value)}
                />
              </div>
            </div>

            <div className="users-select-group submissions-filter-field">
              <label className="users-field-label" htmlFor="submission-area-filter">Área</label>
              <select
                id="submission-area-filter"
                className="users-select"
                value={filters.area}
                onChange={(event) => updateFilter('area', event.target.value)}
              >
                <option value="ALL">Todas as áreas</option>
                {areaOptions.map((area) => <option key={area} value={area}>{area}</option>)}
              </select>
            </div>

            <div className="users-select-group submissions-filter-field">
              <label className="users-field-label" htmlFor="submission-selecao-filter">Situação</label>
              <select
                id="submission-selecao-filter"
                className="users-select"
                value={filters.selecao}
                onChange={(event) => updateFilter('selecao', event.target.value)}
              >
                <option value="ALL">Todas as situações</option>
                {Object.entries(SELECAO_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>

            <ProvaAutomaticaToggle />

            <div className="users-select-group submissions-filter-field submissions-score-filter">
              <label className="users-field-label" htmlFor="submission-score-filter">Pontuação</label>
              <div className="submissions-score-row">
                <select
                  id="submission-score-filter"
                  className="users-select"
                  value={filters.score}
                  onChange={(event) => setFilters((prev) => ({ ...prev, score: event.target.value, scoreMin: '', scoreMax: '' }))}
                >
                  <option value="">Qualquer pontuação</option>
                  <option value={AVERAGE_FILTER.key}>Nota média (1 a 5)</option>
                  <optgroup label="Índices experimentais (0 a 100)">
                    {INDEX_FILTERS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </optgroup>
                  <optgroup label="Notas comportamentais (0 a 5)">
                    {BEHAVIOR_FILTERS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                  </optgroup>
                </select>
                <label className="submissions-score-range">
                  de
                  <input
                    type="text"
                    inputMode="decimal"
                    className="users-text-input"
                    placeholder="0"
                    disabled={!scoreFilter}
                    value={filters.scoreMin}
                    onChange={(event) => updateFilter('scoreMin', event.target.value)}
                    aria-label="Pontuação mínima"
                  />
                </label>
                <label className="submissions-score-range">
                  até
                  <input
                    type="text"
                    inputMode="decimal"
                    className="users-text-input"
                    placeholder={String(scoreFilter?.max || 100)}
                    disabled={!scoreFilter}
                    value={filters.scoreMax}
                    onChange={(event) => updateFilter('scoreMax', event.target.value)}
                    aria-label="Pontuação máxima"
                  />
                </label>
              </div>
              {scoreFilter && <small className="submissions-score-hint">Fichas sem essa nota ficam de fora. A lista fica ordenada da maior para a menor nota.</small>}
            </div>
          </div>

          <div className="submissions-filter-footer">
            <span>{filteredItems.length} de {items.length} fichas exibidas</span>
            <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters} disabled={!hasActiveFilters}>
              Limpar filtros
            </button>
          </div>
        </section>

        {!loading && filteredItems.length === 0 ? (
          <div className="users-feedback users-feedback-empty">
            <AlertTriangle size={20} />
            <div>
              <strong>Nenhuma ficha encontrada</strong>
              <p>Ajuste os filtros para visualizar outras submissões.</p>
            </div>
          </div>
        ) : null}
      </div>

      <section className="users-table-card submissions-table-card">
        {loading ? (
          <div className="users-table-skeleton">
            {Array.from({ length: 5 }).map((_, index) => <div key={index} className="users-skeleton-row" />)}
          </div>
        ) : (
          <OverflowAwareContainer className="users-table-wrapper">
            <table className="users-table submissions-table">
              <thead>
                <tr>
                  <th>Protocolo</th>
                  <th>Candidato</th>
                  <th>Email</th>
                  <th>Telefone</th>
                  <th>Área</th>
                  <th>Experiência em advocacia</th>
                  <th>Nota média</th>
                  <th>Data</th>
                  {scoreFilter && scoreFilter !== AVERAGE_FILTER && <th>{scoreFilter.label}</th>}
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item) => (
                  <tr key={item.id} className={selected?.id === item.id ? 'is-selected' : ''}>
                    <td><span className="submissions-protocol">{item.protocolo}</span></td>
                    <td>
                      <div className="users-name-cell">
                        <div className="users-avatar">{getInitials(item.nome)}</div>
                        <div className="submissions-name-stack">
                          <strong>{item.nome || 'Nome não informado'}</strong>
                          <SelecaoTecnicaChip value={item.selecaoTecnica} />
                        </div>
                      </div>
                    </td>
                    <td>{item.email || '-'}</td>
                    <td>{item.telefone || '-'}</td>
                    <td><span className="users-badge submission-area-badge">{item.areaPretendida || 'Sem área'}</span></td>
                    <td>{nivelExperienciaLabel(item.nivelExperiencia)}</td>
                    <td><span className="submissions-score-value">{formatScore(toNumber(item.notaMedia), 5)}</span></td>
                    <td>{formatDateTime(item.submissionCreatedAt)}</td>
                    {scoreFilter && scoreFilter !== AVERAGE_FILTER && <td><span className="submissions-score-value">{formatScore(toNumber(scoreFilter.get(item)), scoreFilter.max)}</span></td>}
                    <td>
                      <div className="users-actions-group">
                        <button type="button" className="users-icon-btn" title="Visualizar" onClick={() => openDetails(item.id)}>
                          <Eye size={16} />
                        </button>
                        <button type="button" className="users-icon-btn danger" title="Excluir" onClick={() => remove(item.id)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </OverflowAwareContainer>
        )}
      </section>

      <Modal
        isOpen={Boolean(selected && payload)}
        onClose={closeDetails}
        title="Ficha selecionada"
        subtitle={selected ? `${selected.protocolo} • ${selected.email || 'Email não informado'} • ${selected.areaPretendida || 'Área não informada'}` : ''}
        size="submission-details"
      >
        {selected && payload ? (
          <section className="submissions-detail-card">
            <header className="submissions-detail-header">
              <div>
                <span className="users-page-kicker">Ficha selecionada</span>
                <h2>{selected.nome || 'Candidato sem nome'}</h2>
                <p>{selected.protocolo} • {selected.email || 'Email não informado'} • {selected.areaPretendida || 'Área não informada'} • Experiência em advocacia: {nivelExperienciaLabel(selected.nivelExperiencia)}</p>
              </div>
              <div className="submissions-metrics submissions-metrics-explained">
                <ExperimentalMetric
                  title="Aderência estimada"
                  value={selected?.aderencia}
                  meaning="Índice experimental de proximidade ao perfil da área. Não representa probabilidade real de sucesso ou recomendação automática."
                />
                <ExperimentalMetric
                  title="Permanência estimada"
                  value={selected?.permanencia}
                  meaning="Indicador experimental criado por fórmula interna. Não prevê quanto tempo a pessoa permanecerá na empresa."
                />
                <ExperimentalMetric title="Nota média" value={selected?.notaMedia} max={5} meaning={NOTA_MEDIA_INFO} />
              </div>
            </header>

            <ProvasTecnicasFicha ficha={selected} onFichaChange={updateFicha} />

            <div className="submissions-detail-grid">
              <article className="users-detail-card submissions-profile-card">
                <h3>Perfil comportamental</h3>
                <dl className="users-kv-list">
                  <div><dt>Arquétipo</dt><dd>{profileValue(profile, 'archetype', 'Archetype') || '-'}</dd></div>
                  <div><dt>Função ideal</dt><dd>{profileValue(profile, 'idealRole', 'IdealRole') || '-'}</dd></div>
                  <div><dt>Estilo de gestão</dt><dd>{profileValue(profile, 'managementStyle', 'ManagementStyle') || '-'}</dd></div>
                </dl>
              </article>

              <article className="users-detail-card submissions-score-card">
                <h3>Indicadores do questionário</h3>
                <div className="score-intro">
                  <strong>Como ler as notas: </strong>
                  cada dimensão é mostrada na escala de 0 a 5. Uma nota maior indica maior presença daquele atributo nas respostas, o que não significa, necessariamente, um candidato melhor. As faixas são apenas descritivas, sem validação normativa. As respostas e entrevistas devem ser analisadas por pessoas.
                </div>
                {scoreEntries.length > 0 ? scoreEntries.map(([k, v]) => <ScoreBar key={k} label={k} value={v} />) : <p>Nenhum score disponível.</p>}
              </article>
            </div>

            <div className="submissions-profile-grid">
              <ProfileList title="Forças" items={profileValue(profile, 'strengths', 'Strengths')} />
              <ProfileList title="Riscos" items={profileValue(profile, 'risks', 'Risks')} />
              <ProfileList title="Motivadores" items={profileValue(profile, 'motivators', 'Motivators')} />
              <ProfileList title="Desmotivadores" items={profileValue(profile, 'demotivators', 'Demotivators')} />
            </div>

            <article className="users-detail-card submissions-discursive-card">
              <h3>Respostas discursivas</h3>
              <div className="submissions-discursive-list">
                {discursiveEntries(payload?.discursive || {}).map((item) => (
                  <div key={item.key} className="assessment-card submissions-answer-card">
                    <strong>{item.title}</strong>
                    {item.code && <small className="answer-code">{item.code}</small>}
                    {item.text && <p className="answer-question">{item.text}</p>}
                    {item.observe && <p className="answer-observe"><b>O que observar: </b>{item.observe}</p>}
                    <p className="answer-text">{item.value}</p>
                  </div>
                ))}
              </div>
            </article>
          </section>
        ) : null}
      </Modal>
    </div>
  )
}
